(() => {
  'use strict';

  const opening = document.querySelector('#homewoods-opening');
  const shell = document.querySelector('.hw-top-shell');
  if (!opening || !shell || opening.dataset.openingReady === 'true') return;
  opening.dataset.openingReady = 'true';

  const slides = Array.from(opening.querySelectorAll('[data-opening-slide]'));
  const dots = Array.from(opening.querySelectorAll('[data-opening-go]'));
  const counter = opening.querySelector('.hw-opening__counter');
  const room = opening.querySelector('.hw-opening__room');
  const status = opening.querySelector('.hw-opening__status');
  const failedImages = new Set();
  const loadingImages = new Map();
  let activeIndex = 0;
  let requestVersion = 0;
  let scrollFrame = 0;
  let touchStart = null;

  function nextAvailable(direction) {
    for (let distance = 1; distance < slides.length; distance += 1) {
      const index = (activeIndex + direction * distance + slides.length) % slides.length;
      if (!failedImages.has(slides[index].querySelector('img').src)) return index;
    }
    return activeIndex;
  }

  function loadImage(index) {
    const image = slides[index].querySelector('img');
    const source = image.src;
    if (image.complete && image.naturalWidth > 0) return Promise.resolve(true);
    if (failedImages.has(source)) return Promise.resolve(false);
    if (loadingImages.has(source)) return loadingImages.get(source);

    image.loading = 'eager';
    const result = new Promise(resolve => {
      let timeout;
      const done = loaded => {
        image.removeEventListener('load', onLoad);
        image.removeEventListener('error', onError);
        window.clearTimeout(timeout);
        if (image.src !== source) { resolve(false); return; }
        if (!loaded) failedImages.add(source);
        resolve(loaded);
      };
      const onLoad = () => done(image.naturalWidth > 0);
      const onError = () => done(false);
      image.addEventListener('load', onLoad, { once: true });
      image.addEventListener('error', onError, { once: true });
      timeout = window.setTimeout(() => done(image.complete && image.naturalWidth > 0), 15000);
      if (image.complete) done(image.naturalWidth > 0);
    });
    loadingImages.set(source, result);
    return result;
  }

  async function showSlide(index, announce = true) {
    if (!Number.isInteger(index) || index < 0 || index >= slides.length) return;
    const version = ++requestVersion;
    const loaded = await loadImage(index);
    if (version !== requestVersion) return;
    if (!loaded) {
      if (announce) status.textContent = 'This room image is unavailable. Please choose another room.';
      return;
    }

    activeIndex = index;
    slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('is-active', slideIndex === index);
      slide.setAttribute('aria-hidden', String(slideIndex !== index));
    });
    dots.forEach((dot, dotIndex) => {
      if (dotIndex === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
    room.textContent = slides[index].dataset.openingRoom;
    if (announce) status.textContent = `${slides[index].dataset.openingRoom}, image ${index + 1} of ${slides.length}.`;
  }

  document.addEventListener('homewoods:finish-gallery-change', () => {
    ++requestVersion;
    failedImages.clear();
    loadingImages.clear();
    room.textContent = slides[activeIndex].dataset.openingRoom;
    status.textContent = '';
    showSlide(activeIndex, false);
  });

  dots.forEach(dot => dot.addEventListener('click', () => showSlide(Number(dot.dataset.openingGo))));
  opening.querySelector('.hw-opening__previous').addEventListener('click', () => showSlide(nextAvailable(-1)));
  opening.querySelector('.hw-opening__next').addEventListener('click', () => showSlide(nextAvailable(1)));

  opening.addEventListener('keydown', event => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    event.preventDefault();
    showSlide(nextAvailable(event.key === 'ArrowRight' ? 1 : -1));
  });

  opening.addEventListener('touchstart', event => {
    if (event.touches.length !== 1 || event.target.closest('a, button')) {
      touchStart = null;
      return;
    }
    touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
  }, { passive: true });
  opening.addEventListener('touchend', event => {
    if (!touchStart || !event.changedTouches.length) return;
    const dx = event.changedTouches[0].clientX - touchStart.x;
    const dy = event.changedTouches[0].clientY - touchStart.y;
    touchStart = null;
    if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy) * 1.3) {
      showSlide(nextAvailable(dx < 0 ? 1 : -1));
    }
  }, { passive: true });
  opening.addEventListener('touchcancel', () => { touchStart = null; }, { passive: true });

  function updateHeader() {
    scrollFrame = 0;
    shell.classList.toggle('is-scrolled', opening.getBoundingClientRect().bottom <= shell.offsetHeight);
  }

  function queueHeaderUpdate() {
    if (!scrollFrame) scrollFrame = window.requestAnimationFrame(updateHeader);
  }

  function measureHeader() {
    document.documentElement.style.setProperty('--hw-top-shell-height', `${shell.offsetHeight}px`);
    queueHeaderUpdate();
  }

  window.addEventListener('scroll', queueHeaderUpdate, { passive: true });
  window.addEventListener('resize', measureHeader, { passive: true });
  if ('ResizeObserver' in window) new ResizeObserver(measureHeader).observe(shell);
  window.addEventListener('pageshow', measureHeader);

  measureHeader();
  // Warm the next scenes without delaying the first, high-priority image.
  loadImage(0).then(async loaded => {
    const alternatives = await Promise.all(slides.slice(1).map((slide, offset) => loadImage(offset + 1)));
    if (!loaded && activeIndex === 0 && requestVersion === 0) {
      const fallback = alternatives.findIndex(Boolean);
      if (fallback >= 0) showSlide(fallback + 1, false);
    }
  });
})();
